/* ==========================================================================
   GLIMIT — storefront behaviour
   1. Gentle reveal on scroll.
   2. Product gallery: swipe, dots, counter, desktop thumbnails.
   3. Set cards (Single / Together Set) → the product form's variant id.
   4. The photos: a clear "Photo added" confirmation and friendly validation;
      an optional second photo when Two Keychains is chosen.
   5. Mobile sticky add-to-cart bar.
   6. "Choose the Together Set" buttons further down the page.
   No network requests. No analytics. No tracking of any kind.
   ========================================================================== */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollTo = (el, block = 'center') => el && el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block });

  /* ---- 1. reveal ----------------------------------------------------------- */
  const reveal = (root) => {
    const els = root.querySelectorAll('.g-in:not(.is-in)');
    if (!('IntersectionObserver' in window) || reduced) { els.forEach((e) => e.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -6% 0px' });
    els.forEach((e) => io.observe(e));
  };

  /* ---- 2–6. the product page ---------------------------------------------- */
  const pdp = (section) => {
    if (section.dataset.gReady) return;
    section.dataset.gReady = '1';
    const scope = section.closest('.shopify-section') || section;
    const form = document.getElementById(section.dataset.form) || scope.querySelector('form[data-type="add-to-cart-form"]');
    const idInput = form && form.querySelector('input[name="id"]');

    /* gallery */
    const track = section.querySelector('[data-g-track]');
    const slides = track ? [...track.querySelectorAll('.g-gal__slide')] : [];
    const dots = [...section.querySelectorAll('[data-g-go]')];
    const index = section.querySelector('[data-g-index]');
    const thumbs = section.querySelector('[data-g-thumbs]');
    let current = 0;
    const mark = (i) => {
      current = i;
      if (index) index.textContent = String(i + 1);
      dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      thumbs && [...thumbs.children].forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
    };
    const goTo = (i) => {
      if (!track || !slides[i]) return;
      track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: reduced ? 'auto' : 'smooth' });
      mark(i);
    };
    const goToName = (name) => goTo(slides.findIndex((s) => s.dataset.gSlide === name));
    if (track) {
      let raf = 0;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (i !== current && slides[i]) mark(i);
        });
      }, { passive: true });
      dots.forEach((d) => d.addEventListener('click', () => goTo(Number(d.dataset.gGo))));
      if (thumbs) {
        slides.forEach((slide, n) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', `${slide.dataset.label || 'Image'} (${n + 1} of ${slides.length})`);
          const real = slide.querySelector('.g-slot > img, .g-slot__media[poster]');
          if (real && (real.currentSrc || real.src || real.poster)) {
            const img = document.createElement('img');
            img.alt = ''; img.loading = 'lazy'; img.src = real.currentSrc || real.src || real.poster;
            b.appendChild(img);
          } else {
            const art = slide.querySelector('.g-ph__art');
            if (art) {
              const mini = art.cloneNode(true);
              mini.style.cssText = 'position:absolute;inset:0;transform:scale(0.24);transform-origin:center;width:416%;height:416%;left:-158%;top:-158%;pointer-events:none';
              const wrap = document.createElement('span');
              wrap.style.cssText = 'position:absolute;inset:0;overflow:hidden;background:radial-gradient(110% 90% at 50% 40%,#fbf5ec,#e8d9c4)';
              wrap.appendChild(mini);
              b.appendChild(wrap);
            } else {
              const s = document.createElement('span'); s.textContent = slide.dataset.label || String(n + 1); b.appendChild(s);
            }
          }
          b.addEventListener('click', () => goTo(n));
          thumbs.appendChild(b);
        });
        mark(0);
      }
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(Math.min(slides.length - 1, current + 1)); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(Math.max(0, current - 1)); }
      });
    }

    /* the photos — Photo 1 (always required) and, for Two Keychains only, an
       optional Photo 2. Each shows a simple "added" confirmation; the photos
       themselves are never previewed. */
    const box = scope.querySelector('[data-g-upload]');
    const stickyGo = section.querySelector('[data-g-sticky-go]');
    const second = box && box.querySelector('[data-g-second]');
    const removeBtn = box && box.querySelector('[data-g-remove]');
    const MAX_MB = 20;
    let pairMode = !!(box && box.classList.contains('is-pair'));
    const photos = {};
    const syncState = () => {
      const p1 = photos[1], p2 = photos[2];
      if (stickyGo) stickyGo.textContent = p1 && p1.has() ? stickyGo.dataset.add : stickyGo.dataset.photo;
      /* Photo 2 is only sent while Two Keychains is chosen AND a file is
         attached: without a name, a field is never part of the form data. */
      if (p2) {
        const send = pairMode && p2.has();
        if (send) p2.input.name = p2.input.dataset.name; else p2.input.removeAttribute('name');
        if (removeBtn) removeBtn.hidden = !p2.has();
      }
    };
    const setupPhoto = (item) => {
      const input = item.querySelector('input[type="file"]');
      const titleEl = item.querySelector('[data-g-title]');
      const sub = item.querySelector('[data-g-sub]');
      const cta = item.querySelector('[data-g-cta]');
      const error = item.querySelector('[data-g-error]');
      const original = { sub: sub ? sub.textContent : '', cta: cta ? cta.textContent : '' };
      const has = () => !!(input.files && input.files.length);
      const render = () => {
        const d = titleEl.dataset;
        const ready = item.classList.contains('is-ready');
        titleEl.textContent = (ready ? (pairMode ? d.readyPair : d.readySingle) : (pairMode ? d.pair : d.single)) || titleEl.textContent;
      };
      const fail = (msg) => { error.textContent = msg; item.classList.add('is-error'); };
      const reset = () => {
        item.classList.remove('is-ready');
        if (sub) sub.textContent = original.sub;
        if (cta) cta.textContent = original.cta;
        render(); syncState();
      };
      const show = () => {
        item.classList.remove('is-error');
        const f = input.files && input.files[0];
        if (!f) { reset(); return; }
        if (f.size > MAX_MB * 1024 * 1024) {
          input.value = ''; reset();
          fail(`That photo is larger than ${MAX_MB} MB. Please choose a smaller copy.`);
          return;
        }
        item.classList.add('is-ready');
        if (sub) sub.textContent = f.name;
        if (cta) cta.textContent = cta.dataset.change || 'Replace';
        render(); syncState();
      };
      input.addEventListener('change', show);
      return { item, input, has, render, reset, fail, show };
    };
    if (box && !box.dataset.ready) {
      box.dataset.ready = '1';
      box.querySelectorAll('[data-g-photo]').forEach((item) => { photos[item.dataset.gPhoto] = setupPhoto(item); });
      /* a file the browser kept on back/forward */
      Object.values(photos).forEach((p) => { if (p.has()) p.show(); });
      syncState();
      if (removeBtn && photos[2]) removeBtn.addEventListener('click', () => {
        photos[2].input.value = '';
        photos[2].reset();
        photos[2].input.focus({ preventScroll: true });
      });
    }
    const file = photos[1] && photos[1].input;
    const hasPhoto = () => !!(photos[1] && photos[1].has());
    let openTimer = 0;
    const setPair = (pair) => {
      pairMode = pair;
      if (box) box.classList.toggle('is-pair', pair);
      if (second) {
        clearTimeout(openTimer);
        second.classList.toggle('is-open', pair);
        if (pair) openTimer = setTimeout(() => second.classList.add('is-shown'), reduced ? 0 : 340);
        else second.classList.remove('is-shown');
        second.inert = !pair;
        if (pair) second.removeAttribute('aria-hidden'); else second.setAttribute('aria-hidden', 'true');
        if (!pair && photos[2]) photos[2].item.classList.remove('is-error');
      }
      Object.values(photos).forEach((p) => p.render());
      syncState();
    };

    /* set cards */
    const radios = [...section.querySelectorAll('[data-g-sets] input[type="radio"]')];
    const stickyName = section.querySelector('[data-g-sticky-name]');
    const title = section.querySelector('.g-buy__title');
    const applySet = (radio, { slide = true } = {}) => {
      if (!radio) return;
      const pair = radio.dataset.pair === 'true';
      if (idInput) {
        idInput.value = radio.value;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      section.querySelectorAll('[data-g-price]').forEach((el) => { el.textContent = radio.dataset.price; });
      const cmp = section.querySelector('[data-g-compare]');
      if (cmp) { cmp.textContent = radio.dataset.compare || ''; cmp.hidden = !radio.dataset.compare; }
      if (stickyName && title) stickyName.textContent = `${title.textContent.trim()} · ${radio.dataset.title}`;
      setPair(pair);
      try {
        const url = new URL(location.href);
        url.searchParams.set('variant', radio.value);
        history.replaceState(history.state, '', url.toString());
      } catch (_) { /* ignore */ }
      if (slide) goToName(pair ? 'together' : 'single');
    };
    radios.forEach((r) => r.addEventListener('change', () => r.checked && applySet(r)));
    /* the browser may restore a different checked radio on back/forward */
    const checked = radios.find((r) => r.checked);
    if (checked && idInput && checked.value !== idInput.value) applySet(checked, { slide: false });
    else if (checked && (checked.dataset.pair === 'true') !== pairMode) setPair(checked.dataset.pair === 'true');

    /* friendly validation: a message instead of the browser's bubble */
    if (box && file && !box.dataset.validates) {
      box.dataset.validates = '1';
      const missing = () => {
        photos[1].fail(box.dataset.missing || 'Add your photo first — it goes on the keychain.');
        scrollTo(photos[1].item);
      };
      if (form) {
        form.addEventListener('submit', (e) => {
          /* don't send an empty note as a blank line on the order */
          const noteField = form.querySelector('textarea[name^="properties["]');
          if (noteField && !noteField.value.trim()) {
            noteField.disabled = true;
            setTimeout(() => { noteField.disabled = false; }, 0);
          }
          syncState();
          if (hasPhoto()) return;
          e.preventDefault(); e.stopImmediatePropagation();
          missing();
          file.focus({ preventScroll: true });
        }, true);
        file.addEventListener('invalid', (e) => { e.preventDefault(); missing(); });
      }
    }

    /* sticky bar (phones) */
    const sticky = section.querySelector('[data-g-sticky]');
    const buttons = form && form.querySelector('.product-form-buttons');
    if (sticky && buttons) {
      let buttonVisible = true;
      const update = () => {
        /* show once the real button is out of view, never over the footer */
        const nearEnd = document.documentElement.scrollHeight - (scrollY + innerHeight) < 200;
        const show = !buttonVisible && scrollY > 420 && !nearEnd;
        sticky.classList.toggle('is-on', show);
        sticky.setAttribute('aria-hidden', String(!show));
        if (stickyGo) stickyGo.tabIndex = show ? 0 : -1;
      };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => { buttonVisible = en.isIntersecting; update(); }).observe(buttons);
      }
      addEventListener('scroll', update, { passive: true });
      stickyGo && stickyGo.addEventListener('click', () => {
        if (!hasPhoto() && file) { scrollTo(box); file.click(); return; }
        const atc = buttons.querySelector('button[type="submit"], [ref="addToCartButton"]');
        if (form.requestSubmit && atc) form.requestSubmit(atc); else atc && atc.click();
      });
    }

    section._gPick = (want) => {
      const r = radios.find((x) => (x.dataset.pair === 'true') === (want === 'pair') && !x.disabled);
      if (r) { r.checked = true; applySet(r); }
      scrollTo(section.querySelector('[data-g-sets]') || section, 'center');
    };
  };

  /* "Choose the Together Set" etc. — on the product page, select it in place */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-g-pick]');
    if (!a) return;
    const section = document.querySelector('[data-g-pdp]');
    if (!section || !section._gPick) return; /* elsewhere: follow the link */
    e.preventDefault();
    section._gPick(a.dataset.gPick);
  });

  const init = (root = document) => {
    reveal(root);
    root.querySelectorAll('[data-g-pdp]').forEach(pdp);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  /* the theme editor re-renders sections in place */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
